import { Component, OnDestroy, OnInit, effect, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, lastValueFrom, map, catchError, of } from 'rxjs';
import { BreadcrumbService, EnumValues, Note, ModalService, NotesSidebarComponent } from '@card-software/library';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { AuthService } from '@card-software/nexus-utilities';

import { Receipt, ReceiptInsert, ReceiptLine } from '../../../models/receipt/receipt';
import { UserLite } from '../../../models/user-master/user-lite';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { S3Object } from '../../../models/invoice/invoice';
import { ReceiptType } from '../../../models/receipt/receipt-type-enum';
import { InvoiceStatus } from '../../../models/invoice/invoice-status-enum';
import { ReceiptProxyService } from '../../../proxies/order/receipt.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { Business } from '../../../models/finance/business';
import { BusinessSearchCommand } from '../../../models/business-search';
import { BusinessEntityType } from '../../../models/business-type-entities';
import { ItemMaster, ItemSearchCommand } from '../../../models/item/item-master/item-master';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ExchangeRateProxyService } from '../../../proxies/finance/exchange-rate.proxy.service';
import { Currency } from '../../../models/purchase-order/currency';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { EnumService } from '../../../services/enum.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { IMEXAdminGroup, IMEXViewerGroup } from '../../../models/roles/roles-group';

@Component({
  selector: 'app-receipt-detail',
  templateUrl: './receipt-detail.component.html',
  styleUrl: './receipt-detail.component.scss',
})
export class ReceiptDetailComponent implements OnInit, OnDestroy {
  public receipt?: Receipt;
  public isNewReceipt = true;
  public receiptType: ReceiptType = ReceiptType.Fiscal;
  public isLoading = 0;

  providersToDisplay: Business[] = [];
  // Item picker (mirrors the Purchase Order pattern): pick an item to add a pre-filled line.
  filteredItems: ItemMaster[] = [];
  public searchItemInput = new FormControl<string>('');
  uoms: EnumValues = {};
  // UoM options for the p-select (built from the UnitOfMeasure enum).
  uomOptions: { label: string; value: number }[] = [];
  // Tax-rate options (decimal fractions). Clearable — leaving it empty means no tax (null).
  readonly taxRateOptions = [{ label: '15%', value: 0.15 }];
  // Currency options for the p-select (built from the Currency enum).
  currencyOptions: { label: string; value: number }[] = [];
  statusEnum: EnumValues = {};

  readonly Currency = Currency;

  /** UnitOfMeasure.unit — default UoM for a line added without an item master. */
  private readonly DEFAULT_UOM = 2;

  totalAmount = signal<number>(0);
  // Notes use the shared csl-standard-notes component (same as Purchase Order).
  notes: Note[] = [];
  userLite: UserLite | null = null;

  // Form controls
  public inputProvider = new FormControl<Business | null>(null, [Validators.required]);
  // Payment terms (PaymentTermsProvider). Not Validators.required — the API resolves it from the provider's
  // default when left empty and only blocks when the provider has none either.
  public paymentTerm = new FormControl<string | null>(null);
  public providerInvoiceNumber = new FormControl<string>('', { nonNullable: true });
  public fiscalInvoiceNumber = new FormControl<string>('', { nonNullable: true });
  public issuedDate = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  // Currency defaults to USD; exchange rate is forced to 1 for USD and required for the other currencies.
  public currency = new FormControl<number>(Currency.USD, { nonNullable: true, validators: [Validators.required] });
  public exchangeRateToUSD = new FormControl<number>(1, { nonNullable: true, validators: [Validators.required, Validators.min(0.0001)] });
  public formArrayItems = new FormArray<FormGroup>([]);
  public status = new FormControl<number>(InvoiceStatus.Draft, { nonNullable: true, validators: [Validators.required] });

  public receiptForm = new FormGroup({
    provider: this.inputProvider,
    paymentTerm: this.paymentTerm,
    providerInvoiceNumber: this.providerInvoiceNumber,
    fiscalInvoiceNumber: this.fiscalInvoiceNumber,
    issuedDate: this.issuedDate,
    currency: this.currency,
    exchangeRateToUSD: this.exchangeRateToUSD,
    items: this.formArrayItems,
  });

  private subscription = new Subscription();

  private receiptProxyService = inject(ReceiptProxyService);
  private businessProxyService = inject(BusinessProxyService);
  private itemMasterProxyService = inject(ItemMasterProxyService);
  private exchangeRateProxyService = inject(ExchangeRateProxyService);
  private localeHelperService = inject(LocaleHelperService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private modalService = inject(ModalService);
  private enumService = inject(EnumService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private breadcrumbService = inject(BreadcrumbService);

  readonly ReceiptType = ReceiptType;

  constructor() {
    effect(() => {
      this.uoms = this.enumService.getEnum('UnitOfMeasure', 'itemEnums')?.values;
      this.uomOptions = Object.entries(this.uoms ?? {}).map(([key, label]) => ({ label: String(label), value: Number(key) }));
      const currencyEnum = this.enumService.getEnum('Currency', 'financeEnums')?.values ?? {};
      this.currencyOptions = Object.entries(currencyEnum).map(([key, label]) => ({ label: String(label), value: Number(key) }));
      this.statusEnum = this.enumService.getEnum('CustomerOrderStatus', 'orderEnums')?.values;
    });
  }

  ngOnInit(): void {
    this.userLite = this.userAuthenticationService.userLite;
    this.subscription.add(this.formArrayItems.valueChanges.subscribe(() => this.recomputeTotal()));

    // Default the payment terms from the selected provider (overridable) — mirrors how a PO defaults them.
    this.subscription.add(
      this.inputProvider.valueChanges.subscribe((provider) => {
        const providerTerm = provider?.providerDetails?.paymentTermsCode;
        if (providerTerm && !this.paymentTerm.value) {
          this.paymentTerm.setValue(providerTerm);
        }
      }),
    );

    // Picking a currency pulls that day's rate from Finance (USD is the reference currency — always 1).
    this.subscription.add(this.currency.valueChanges.subscribe(() => this.fetchDailyExchangeRate()));

    this.activatedRoute.queryParams.subscribe((params) => {
      const id = params['id'];
      const type = params['type'];

      if (!id) {
        this.cancel();
        return;
      }

      if (id === 'new') {
        this.isNewReceipt = true;
        this.breadcrumbService.setCurrent('Common_New');
        this.receiptType = type !== undefined && type !== null ? (Number(type) as ReceiptType) : ReceiptType.Fiscal;
        this.applyTypeRules();
        // A new document carries no currency yet — start on USD and take that day's rate.
        this.fetchDailyExchangeRate();
        this.applyEditLock();
      } else {
        this.isNewReceipt = false;
        this.getReceipt(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  canEdit(): boolean {
    if (AuthService.hasRole(IMEXAdminGroup.InvoiceAdmin)) {
      return true;
    }
    if (AuthService.hasRole(IMEXViewerGroup.InvoiceViewer)) {
      return false;
    }
    return false;
  }

  /** The document is only open for edits while it is a Draft — submitting freezes it. */
  get isDraft(): boolean {
    return this.isNewReceipt || (this.receipt?.status ?? InvoiceStatus.Draft) === InvoiceStatus.Draft;
  }

  /**
   * Content edits (provider, dates, currency, lines, files) need both the role and a Draft document. Past
   * Draft the document is frozen: only the status can still move forward.
   */
  canEditDocument(): boolean {
    return this.canEdit() && this.isDraft;
  }

  /** Freezes the document once it is past Draft (or the user is read-only). */
  private applyEditLock(): void {
    if (this.canEditDocument()) {
      return;
    }
    this.receiptForm.disable({ emitEvent: false });
    this.searchItemInput.disable({ emitEvent: false });
    if (!this.canEdit()) {
      this.status.disable({ emitEvent: false });
    }
  }

  get isFiscal(): boolean {
    return this.receiptType === ReceiptType.Fiscal;
  }

  get isUsd(): boolean {
    return this.currency.value === Currency.USD;
  }

  /**
   * Pulls the day's rate for the selected currency from Finance. USD is the reference currency, so its rate is
   * always 1.00 and no call is made. When no rate is published for the day the field is left at 0 so the
   * required/min validators force the user to type one in.
   */
  private fetchDailyExchangeRate(): void {
    if (this.isUsd) {
      this.exchangeRateToUSD.setValue(1, { emitEvent: false });
      return;
    }

    this.isLoading++;
    this.exchangeRateProxyService.getByCurrency(this.currency.value).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data?.rateToUSD) {
          this.exchangeRateToUSD.setValue(response.data.rateToUSD, { emitEvent: false });
          return;
        }
        this.exchangeRateToUSD.setValue(0, { emitEvent: false });
        GlobalMessageUtil.warn(
          'Receipt: Warning',
          'No exchange rate found for the selected currency. Please enter the exchange rate manually.',
        );
      },
      error: () => {
        this.isLoading--;
        this.exchangeRateToUSD.setValue(0, { emitEvent: false });
      },
    });
  }

  /** Label of the selected currency (from the Currency enum), shown next to totals in the summary. */
  get currencyLabel(): string {
    return this.currencyOptions.find((o) => o.value === this.currency.value)?.label ?? '';
  }

  typeLabel(): string {
    return this.isFiscal ? 'Fiscal' : 'Purchase';
  }

  /** Fiscal receipts require the provider invoice number; Purchase receipts carry no provider/fiscal number. */
  private applyTypeRules(): void {
    if (this.isFiscal) {
      this.providerInvoiceNumber.setValidators([Validators.required]);
      this.providerInvoiceNumber.enable({ emitEvent: false });
      this.fiscalInvoiceNumber.enable({ emitEvent: false });
    } else {
      this.providerInvoiceNumber.clearValidators();
      this.providerInvoiceNumber.setValue('', { emitEvent: false });
      this.fiscalInvoiceNumber.setValue('', { emitEvent: false });
      this.providerInvoiceNumber.disable({ emitEvent: false });
      this.fiscalInvoiceNumber.disable({ emitEvent: false });
    }
    this.providerInvoiceNumber.updateValueAndValidity({ emitEvent: false });
  }

  // ── Provider ──────────────────────────────────────────────────────────────
  searchProvider(event: AutoCompleteCompleteEvent): void {
    const query = event.query;
    const searchCommand: BusinessSearchCommand = {
      name: query,
      code: '',
      businessTypes: [BusinessEntityType.Provider],
    };
    this.businessProxyService.search(searchCommand).subscribe({
      next: (response) => {
        this.providersToDisplay = response.data?.items ?? [];
      },
      error: (e) => {
        console.error(e);
        this.providersToDisplay = [];
      },
    });
  }

  // ── Line items ────────────────────────────────────────────────────────────
  /**
   * Builds a line form. When `itemMaster` is provided (item picked from the master, like a PO), itemCode,
   * description, classification and UoM are derived from it. A line added without an item defaults its UoM to
   * `unit`; the user then picks its classification (ItemClassification master data) and UoM from the dropdowns.
   */
  private buildLineForm(line?: ReceiptLine, itemMaster?: ItemMaster): FormGroup {
    const itemCode = itemMaster?.itemCode ?? line?.itemCode ?? null;
    const description = itemMaster
      ? this.localeHelperService.getLocalizedValue(itemMaster.itemLongDisplayName)
      : (line?.description ?? null);
    const classification = itemMaster?.itemClassificationCode ?? line?.itemClassification ?? null;
    const unitOfMeasure = itemMaster?.unitOfMeasure ?? line?.unitOfMeasure ?? this.DEFAULT_UOM;
    // For an item-master line, classification + UoM come from the master and are not changeable; a manual
    // line (no item) lets the user pick them. Disabled controls are still captured via getRawValue() on save.
    const fromItem = !!itemCode;
    // unitPrice is the stored driver (like the AP): total = quantity × unitPrice, and editing the total
    // back-solves unitPrice. A persisted line only carries total, so derive unitPrice = total / quantity.
    const quantity = line?.quantity ?? 0;
    const unitPrice = quantity > 0 ? (line?.total ?? 0) / quantity : (line?.total ?? 0);

    return new FormGroup({
      itemCode: new FormControl<string | null>(itemCode),
      description: new FormControl<string | null>(description),
      quantity: new FormControl<number>(quantity, [Validators.required, Validators.min(0.0001)]),
      unitOfMeasure: new FormControl<number>({ value: unitOfMeasure, disabled: fromItem }, [Validators.required]),
      unitPrice: new FormControl<number>(unitPrice, [Validators.required, Validators.min(0)]),
      taxRate: new FormControl<number | null>(line?.taxRate ?? null, [Validators.min(0), Validators.max(0.9999)]),
      itemClassification: new FormControl<string | null>({ value: classification, disabled: fromItem }, [Validators.required]),
    });
  }

  /** Item-master search for the line picker (mirrors the PO: name search, or itemCode for an `n-n` pattern). */
  async getItemOptions(event: AutoCompleteCompleteEvent): Promise<void> {
    const term = event.query.trim();
    const isCodePattern = /^\d+-\d+$/.test(term);
    const dataSearch: ItemSearchCommand = {
      name: isCodePattern ? '' : term,
      itemCode: isCodePattern ? term : '',
    };

    try {
      this.filteredItems = await lastValueFrom(
        this.itemMasterProxyService.postItemSearch(dataSearch, true).pipe(
          map((response) =>
            (response.data?.items ?? []).map((item) => ({
              ...item,
              fieldToDisplay: item.itemCode + ' ' + this.localeHelperService.getLocalizedValue(item.itemLongDisplayName),
            })),
          ),
          catchError(() => of([] as ItemMaster[])),
        ),
      );
    } catch {
      this.filteredItems = [];
    }
  }

  /** Adds a line pre-filled from the picked item master, then clears the search box. */
  onSelectItem(event: AutoCompleteSelectEvent): void {
    this.formArrayItems.push(this.buildLineForm(undefined, event.value as ItemMaster));
    this.searchItemInput.reset();
    this.recomputeTotal();
  }

  /** Adds a blank line (no item): UoM defaults to `unit`, classification is picked from the dropdown. */
  addLine(): void {
    this.formArrayItems.push(this.buildLineForm());
  }

  removeLine(index: number): void {
    this.formArrayItems.removeAt(index);
    this.recomputeTotal();
  }

  /** Line total = quantity × unit price (derived, like the AP). */
  lineTotal(line: FormGroup): number {
    return (Number(line.get('quantity')?.value) || 0) * (Number(line.get('unitPrice')?.value) || 0);
  }

  /** Editing the total back-solves the unit price (total ÷ quantity) — the two are tied by the quantity. */
  onLineTotalChange(line: FormGroup, total: number | null): void {
    const qty = Number(line.get('quantity')?.value) || 0;
    const unit = qty > 0 ? (total ?? 0) / qty : (total ?? 0);
    line.get('unitPrice')?.setValue(Math.round(unit * 10000) / 10000, { emitEvent: false });
    this.recomputeTotal();
  }

  private recomputeTotal(): void {
    const total = this.formArrayItems.controls.reduce((acc, group) => acc + this.lineTotal(group as FormGroup), 0);
    this.totalAmount.set(total);
  }

  // ── Notes (shared csl-standard-notes + NotesSidebarComponent modal, same as Purchase Order) ─────────────
  onViewMore(): void {
    this.openNotesModal();
  }

  private openNotesModal(): void {
    const modalData = {
      notes: this.notes,
      userLite: this.userLite,
      viewOnly: !this.canEdit(),
    };

    this.modalService.open(NotesSidebarComponent, {}, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'noteAdded') {
          this.notes = result.data.notes;
        }
      },
      error: (error) => {
        console.error('Error handling notes modal result:', error);
      },
    });
  }

  // ── Load ──────────────────────────────────────────────────────────────────
  private getReceipt(id: string): void {
    this.isLoading++;
    this.receiptProxyService.getReceipt(id).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data) {
          this.receipt = response.data;
          this.breadcrumbService.setCurrent(this.receipt.number);
          this.receiptType = this.receipt.type;
          this.applyTypeRules();
          this.status.setValue(this.receipt.status);
          this.inputProvider.setValue(this.receipt.provider ?? null);
          this.paymentTerm.setValue(this.receipt.paymentTermCode ?? null);
          this.providerInvoiceNumber.setValue(this.receipt.providerInvoiceNumber ?? '');
          this.fiscalInvoiceNumber.setValue(this.receipt.fiscalInvoiceNumber ?? '');
          this.issuedDate.setValue(this.parseDate(this.receipt.issuedDate ?? '') as string);
          this.notes = this.receipt.notes ?? [];

          // A document keeps the rate it was booked at. One that carries no currency falls back to USD, and a
          // draft with no rate yet takes the day's published rate (1.00 when it is USD).
          this.currency.setValue(this.receipt.currency ?? Currency.USD, { emitEvent: false });
          if (this.receipt.exchangeRateToUSD) {
            this.exchangeRateToUSD.setValue(this.receipt.exchangeRateToUSD, { emitEvent: false });
          } else if (this.isDraft) {
            this.fetchDailyExchangeRate();
          } else {
            this.exchangeRateToUSD.setValue(1, { emitEvent: false });
          }

          this.formArrayItems.clear();
          (this.receipt.items ?? []).forEach((line) => this.formArrayItems.push(this.buildLineForm(line)));
          this.recomputeTotal();

          this.applyEditLock();
        }
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  save(): void {
    this.receiptForm.markAllAsTouched();

    if (this.inputProvider.invalid) {
      GlobalMessageUtil.error('Error', 'Please select a provider');
      return;
    }
    if (this.formArrayItems.length === 0) {
      GlobalMessageUtil.error('Error', 'Please add at least one line');
      return;
    }
    if (this.receiptForm.invalid) {
      console.log('Form invalid:', this.receiptForm.errors, this.receiptForm.controls);
      GlobalMessageUtil.error('Error', 'Please fill all required fields');
      return;
    }

    const items: ReceiptLine[] = this.formArrayItems.getRawValue().map((line) => ({
      itemCode: line['itemCode'] ?? undefined,
      description: line['description'] ?? undefined,
      quantity: Number(line['quantity']) || 0,
      unitOfMeasure: Number(line['unitOfMeasure']) || 0,
      taxRate: line['taxRate'] !== null && line['taxRate'] !== undefined ? Number(line['taxRate']) : undefined,
      // total is derived from quantity × unit price (the line stores total; Finance re-derives unit price).
      total: (Number(line['quantity']) || 0) * (Number(line['unitPrice']) || 0),
      itemClassification: line['itemClassification'],
    }));

    const payload: ReceiptInsert = {
      type: this.receiptType,
      status: this.status.value,
      providerReference: this.inputProvider.value?.id ?? '',
      paymentTermCode: this.paymentTerm.value ?? undefined,
      providerInvoiceNumber: this.isFiscal ? this.providerInvoiceNumber.value || undefined : undefined,
      fiscalInvoiceNumber: this.isFiscal ? this.fiscalInvoiceNumber.value || undefined : undefined,
      items,
      totalAmount: this.totalAmount(),
      issuedDate: this.issuedDate.value,
      currency: this.currency.value,
      exchangeRateToUSD: this.isUsd ? 1 : this.exchangeRateToUSD.value,
      notes: this.notes,
    };

    if (this.isNewReceipt) {
      this.receiptProxyService.createReceipt(payload).subscribe({
        next: (resp) => {
          if (resp.data) {
            this.receipt = resp.data;
            this.isNewReceipt = false;
            GlobalMessageUtil.success('Success', `Receipt ${resp.data.number} has been created successfully`);
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { id: this.receipt.id, type: null },
              queryParamsHandling: 'merge',
            });
          }
        },
        error: (e) => console.error(e),
      });
    } else {
      this.receiptProxyService.updateReceipt(this.receipt!.id, payload).subscribe({
        next: () => {
          GlobalMessageUtil.success('Success', `Receipt ${this.receipt?.number} has been updated successfully`);
          this.router.navigate(['/receipts']);
        },
        error: (e) => console.error(e),
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/receipts']);
  }

  // ── Files ─────────────────────────────────────────────────────────────────
  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    if (!this.receipt?.id) {
      GlobalMessageUtil.warn('Warning', 'Please save the receipt before attaching files');
      input.value = '';
      return;
    }

    const fileList = Array.from(input.files);
    try {
      const uploaded = await lastValueFrom(this.receiptProxyService.uploadFiles(fileList, this.receipt.id));
      this.receipt.files = [...(this.receipt.files ?? []), ...(uploaded ?? [])];
      GlobalMessageUtil.success('Success', 'Files uploaded successfully');
    } catch (error) {
      console.error(error);
    } finally {
      input.value = '';
    }
  }

  viewFile(presignedUrl?: string): void {
    if (presignedUrl) window.open(presignedUrl, '_blank');
  }

  deleteFile(file: S3Object): void {
    if (!file || !this.receipt?.id) return;
    this.receiptProxyService.deleteFile(this.receipt.id, file).subscribe({
      next: () => {
        this.receipt!.files = (this.receipt!.files ?? []).filter((f) => f.name !== file.name);
        GlobalMessageUtil.success('Success', 'File deleted successfully');
      },
      error: (e) => console.error(e),
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  getUnitName(uomKey: number | null): string {
    if (uomKey === null || uomKey === undefined) return '';
    return this.uoms?.[uomKey] || '';
  }

  parseDate(date: Date | string): Date | string {
    if (!date) return '';
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
  }
}
